import { useCallback, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { useAnimationFrame } from '@/hooks/useAnimationFrame';
import { ktColor } from './color';
import type { TileLayerKey } from '@/hooks/useMapSettings';
import { buildWindGrid, sampleWindGrid } from './windField';
import type { IDWResult } from './types';
import type { IDWStation } from './types';
import { CanvasOverlayLayer } from './CanvasOverlay';

const N_PARTICLES = 875;
const TRAIL_LEN = 6;
const MAX_AGE = 90;

interface Particle {
  x: number;
  y: number;
  age: number;
  maxAge: number;
  trail: { x: number; y: number; kt: number }[];
}

/** A constant wind vector, used for the wildfire page's single recorded reading. */
export interface UniformWind {
  /** Wind speed in knots. */
  speedKt: number;
  /** Wind direction in degrees (meteorological — where wind comes from). */
  dirDeg: number;
}

interface WindParticleLayerProps {
  show: boolean;
  /** "Stations" mode: an IDW-interpolated curling field (forecast page). */
  stations?: IDWStation[];
  /** "Uniform" mode: a single constant vector flowing everywhere (fire page). */
  uniform?: UniformWind | null;
  /**
   * Active base map. Satellite imagery is dark and busy, so the particles
   * need a brighter ramp, more weight and a dark halo to stay readable;
   * over the pale street tiles the same treatment would look heavy.
   */
  basemap?: TileLayerKey;
}

function spawnParticle(w: number, h: number): Particle {
  return {
    x: Math.random() * w,
    y: Math.random() * h,
    age: Math.floor(Math.random() * MAX_AGE),
    maxAge: MAX_AGE + Math.floor(Math.random() * 40),
    trail: [],
  };
}

/** Convert a uniform speed/direction into a constant sample result. */
function uniformResult(uni: UniformWind): IDWResult {
  const speedMs = uni.speedKt / 1.94384;
  const rad = (uni.dirDeg * Math.PI) / 180;
  return {
    u: -Math.sin(rad) * speedMs,
    v: -Math.cos(rad) * speedMs,
    speedKt: uni.speedKt,
    fade: 1,
  };
}

/**
 * Animated wind particle field — ported from sites/my-fdi's WindParticleLayer
 * but decoupled from Redux and driven entirely by props. Renders flowing,
 * speed-coloured particle trails onto a dedicated canvas pane.
 *
 * Two field sources feed one renderer:
 *  - `stations`: IDW-interpolated, so the flow curls between stations.
 *  - `uniform`: a single constant vector (same aesthetic, no curl).
 *
 * Must be mounted as a child of a react-leaflet `MapContainer` (it uses
 * `useMap()`).
 */
export function WindParticleLayer({ show, stations, uniform, basemap = 'street' }: WindParticleLayerProps) {
  const map = useMap();

  const layerRef = useRef<CanvasOverlayLayer | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  // Current sampler: maps a pixel position to a wind result, or null when no
  // data is available yet (particles then just idle/fade).
  const sampleRef = useRef<((x: number, y: number) => IDWResult) | null>(null);

  const stationsRef = useRef<IDWStation[] | undefined>(stations);
  const uniformRef = useRef<UniformWind | null | undefined>(uniform);

  // Rebuild the sampler from the latest props + current viewport.
  const rebuildSampler = useCallback(() => {
    const uni = uniformRef.current;
    if (uni) {
      const constant = uniformResult(uni);
      sampleRef.current = () => constant;
      return;
    }

    const sts = stationsRef.current;
    if (!sts || !sts.length) {
      sampleRef.current = null;
      return;
    }

    const size = map.getSize();
    const grid = buildWindGrid(size.x, size.y, sts, (x: number, y: number) => {
      const latlng = map.containerPointToLatLng([x, y]);
      return { lat: latlng.lat, lng: latlng.lng };
    });
    sampleRef.current = (x: number, y: number) => sampleWindGrid(grid, x, y);
  }, [map]);

  // Add/remove the canvas overlay layer when visibility toggles.
  useEffect(() => {
    if (!show) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }

    const layer = new CanvasOverlayLayer({ paneName: 'windPane', zIndex: 450 });
    layer.onDraw = () => {}; // Drawing happens in the animation loop.
    layer.addTo(map);
    layerRef.current = layer;

    const size = map.getSize();
    particlesRef.current = Array.from({ length: N_PARTICLES }, () =>
      spawnParticle(size.x, size.y),
    );

    rebuildSampler();

    const onMove = () => {
      rebuildSampler();
      const sz = map.getSize();
      particlesRef.current = Array.from({ length: N_PARTICLES }, () =>
        spawnParticle(sz.x, sz.y),
      );
    };
    map.on('moveend', onMove);

    return () => {
      map.off('moveend', onMove);
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, show, rebuildSampler]);

  // Rebuild the sampler when the data changes (debounced for smooth timeline
  // scrubbing on the forecast page).
  const rebuildTimeoutRef = useRef<number>(0);
  useEffect(() => {
    stationsRef.current = stations;
    uniformRef.current = uniform;
    if (!show) return;
    clearTimeout(rebuildTimeoutRef.current);
    rebuildTimeoutRef.current = window.setTimeout(() => {
      rebuildSampler();
    }, 16);
    return () => clearTimeout(rebuildTimeoutRef.current);
  }, [stations, uniform, show, rebuildSampler]);

  // Animation loop.
  useAnimationFrame(() => {
    const layer = layerRef.current;
    const sample = sampleRef.current;
    if (!layer || !sample) return;

    const canvas = layer.getCanvas();
    const ctx = layer.getContext();
    if (!canvas || !ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    // Zoom-aware speed & density. At zoom 6 (SA overview) we use full speed
    // and all particles; each zoom level in reduces speed by 23% and particle
    // count by 10%.
    const zoom = map.getZoom();
    const zoomDelta = zoom - 6;
    const speedScale = 0.5 * Math.pow(0.77, zoomDelta);
    const activeCount = Math.max(50, Math.round(N_PARTICLES * Math.pow(0.9, zoomDelta)));

    const onSatellite = basemap === 'satellite';

    // Fade existing trails. Satellite clears faster — a long faint smear
    // over photographic terrain just looks like noise.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = onSatellite ? 'rgba(0,0,0,0.075)' : 'rgba(0,0,0,0.042)';
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'source-over';

    const particles = particlesRef.current;
    for (let pi = 0; pi < Math.min(activeCount, particles.length); pi++) {
      const p = particles[pi];
      if (!p) continue;
      const w = sample(p.x, p.y);
      p.trail.push({ x: p.x, y: p.y, kt: w.speedKt });
      if (p.trail.length > TRAIL_LEN) p.trail.shift();

      p.x += w.u * speedScale;
      p.y -= w.v * speedScale;
      p.age++;

      if (
        p.age > p.maxAge ||
        p.x < -20 ||
        p.x > W + 20 ||
        p.y < -20 ||
        p.y > H + 20 ||
        w.fade <= 0
      ) {
        Object.assign(p, spawnParticle(W, H), { age: 0 });
        continue;
      }

      if (p.trail.length < 2) continue;
      const la = Math.sin((p.age / p.maxAge) * Math.PI);
      if (la < 0.01) continue;

      const kt = p.trail[p.trail.length - 1]?.kt ?? 0;
      const alpha = onSatellite
        ? la * Math.min(1, 0.6 + kt / 60) * w.fade
        : la * Math.min(0.85, 0.3 + kt / 60) * w.fade;

      ctx.beginPath();
      const first = p.trail[0]!;
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < p.trail.length; i++) {
        const pt = p.trail[i]!;
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.lineTo(p.x, p.y);
      const width = (onSatellite ? 1.8 : 1.0) + Math.min(kt, 50) / 40;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Dark halo first, then the coloured stroke on top. This is what makes
      // the field readable over imagery — a bright line on a bright roof or a
      // pale field vanishes without it. Two strokes of an already-built path
      // rather than shadowBlur, which is far slower at ~875 particles/frame.
      if (onSatellite) {
        ctx.strokeStyle = `rgba(0,0,0,${(alpha * 0.6).toFixed(3)})`;
        ctx.lineWidth = width + 2.5;
        ctx.stroke();
      }

      ctx.strokeStyle = ktColor(kt, alpha, onSatellite ? 'satellite' : 'default');
      ctx.lineWidth = width;
      ctx.stroke();
    }
  }, show);

  return null;
}

export default WindParticleLayer;
